import { COLORS } from '@/config/colors';
import type { ProceduralDef } from '../types';
import { shade } from './draw-utils';
import {
  LOOT_FRAMES, REPAIR_GREEN, SPARK, brilliantCut, heaterPlate, hexCut, lootGlow, lootHalo,
  mediGem, sparkle,
} from './loot-draw';

/*
 * Big loot (polish 1, docs/04): a high-value gem piece and a ×2 supply drop
 * get their OWN silhouette instead of a scaled-up sprite. Same loot
 * language — faceted cuts, white sparkle, soft glow; "more" reads as MORE
 * cuts (a CLUSTER of 3, a HOARD pile of 6 with a halo ring) or two stacked
 * plates / medi-gems. Never a spike, a dark core or a hot rim.
 */

type Ctx = CanvasRenderingContext2D;

/** One cut in an arrangement, as fractions of the texture size. */
interface Cut {
  x: number;
  y: number;
  r: number;
}

/** Three cuts: a big one in front of two small ones. */
const CLUSTER: ReadonlyArray<Cut> = [
  { x: -0.2, y: 0.14, r: 0.17 },
  { x: 0.2, y: 0.14, r: 0.17 },
  { x: 0, y: -0.06, r: 0.26 },
];
/** A 1-2-3 pile, drawn back (top) to front (bottom row). */
const HOARD: ReadonlyArray<Cut> = [
  { x: 0, y: -0.24, r: 0.16 },
  { x: -0.14, y: -0.01, r: 0.15 },
  { x: 0.14, y: -0.01, r: 0.15 },
  { x: -0.27, y: 0.22, r: 0.14 },
  { x: 0, y: 0.22, r: 0.14 },
  { x: 0.27, y: 0.22, r: 0.14 },
];
const GEM_SIZE = 48;
const SUPPLY_SIZE = 64;
/** Outline of the small cuts (the single pickup uses 4) */
const SMALL_LINE = 3;
const HALO_R = 0.46;
/** Back copy of a ×2 supply: darker, up-left of the front one */
const BACK_SHADE = 0.8;

type CutFn = (ctx: Ctx, cx: number, cy: number, r: number, color: number, line?: number) => void;

/** Draw an arrangement + its wandering sparkle(s). */
function pile(
  ctx: Ctx, w: number, frame: number, cuts: ReadonlyArray<Cut>, cut: CutFn, color: number,
  halo: boolean,
): void {
  const c = w / 2;
  lootGlow(ctx, c, c, w * 0.5);
  if (halo) lootHalo(ctx, c, c, w * HALO_R, color);
  for (const k of cuts) cut(ctx, c + k.x * w, c + k.y * w, k.r * w, color, SMALL_LINE);
  // The sparkle hops from cut to cut; hoards carry a second, smaller one
  const lit = (i: number, size: number): void => {
    const k = cuts[i % cuts.length]!;
    const [sx, sy] = SPARK[(frame + i) % SPARK.length]!;
    sparkle(ctx, c + (k.x + sx * k.r) * w, c + (k.y + sy * k.r) * w, size);
  };
  // Front cut first, then walking back through the pile frame by frame
  lit(cuts.length * SPARK.length - 1 - frame, 3.5);
  if (halo) lit(frame + 1, 2.5);
}

function gemDef(key: string, cuts: ReadonlyArray<Cut>, cut: CutFn, color: number, halo: boolean): ProceduralDef {
  return {
    key, width: GEM_SIZE, height: GEM_SIZE, anchorX: 0.5, anchorY: 0.5, frames: LOOT_FRAMES,
    draw(ctx, w, _h, frame) {
      pile(ctx, w, frame, cuts, cut, color, halo);
    },
  };
}

export const gemYellowCluster = gemDef('pickup/gem-yellow-cluster', CLUSTER, brilliantCut, COLORS.gemYellow, false);
export const gemYellowHoard = gemDef('pickup/gem-yellow-hoard', HOARD, brilliantCut, COLORS.gemYellow, true);
export const gemRedCluster = gemDef('pickup/gem-red-cluster', CLUSTER, hexCut, COLORS.gemRed, false);
export const gemRedHoard = gemDef('pickup/gem-red-hoard', HOARD, hexCut, COLORS.gemRed, true);

/** ×2 supply: two copies (back darker, up-left), halo ring, sparkle on the front. */
function supplyDef(
  key: string, color: number, draw: (ctx: Ctx, x: number, y: number, s: number, color: number) => void,
): ProceduralDef {
  return {
    key, width: SUPPLY_SIZE, height: SUPPLY_SIZE, anchorX: 0.5, anchorY: 0.5, frames: LOOT_FRAMES,
    draw(ctx, w, _h, frame) {
      const c = w / 2;
      const s = 15;
      lootGlow(ctx, c, c, w * 0.48);
      lootHalo(ctx, c, c, w * HALO_R, color);
      draw(ctx, c - 7, c - 6, s - 1, shade(color, BACK_SHADE));
      draw(ctx, c + 5, c + 5, s, color);
      const [sx, sy] = SPARK[frame]!;
      sparkle(ctx, c + 5 + sx * s, c + 5 + sy * s, 4);
    },
  };
}

export const pickupShieldX2 = supplyDef('pickup/shield-x2', COLORS.hudArmor, heaterPlate);
export const pickupRepairX2 = supplyDef('pickup/repair-x2', REPAIR_GREEN, mediGem);
